import React from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { pickCollectionNode, resolveFaqCollection } from "@utils/cmsCollections"
import { getPrototypePageSlice } from "@utils/prototypeSlices"

const prototypeSlice = getPrototypePageSlice("howItWorks")

const howItWorksMobileStyles = `
.cms-page:not(.cms-page--home) .steps-hero .step-lede,
.cms-page:not(.cms-page--home) .steps-hero .step-list,
.cms-page:not(.cms-page--home) .steps-hero .step-list li,
.cms-page:not(.cms-page--home) .steps-hero .step-body,
.cms-page:not(.cms-page--home) .steps-hero .time-strip-sub {
  color: #334155 !important;
}

.cms-page:not(.cms-page--home) .steps-hero .step-h,
.cms-page:not(.cms-page--home) .steps-hero .step-num-big,
.cms-page:not(.cms-page--home) .steps-hero .time-strip-num {
  color: #0b1f2a !important;
}

.cms-page:not(.cms-page--home) .steps-hero .step-row.active .step-num-big,
.cms-page:not(.cms-page--home) .steps-hero .step-row.done .step-num-big {
  color: #fff !important;
}

/* Everything inside the step graphics is an illustration, not an action.
   pointer-events:none blocks user clicks; the scripted auto-demo still runs
   because programmatic .click() bypasses pointer-events. */
.cms-page .steps-hero .sm-btn,
.cms-page .steps-hero .fb-prev-btn,
.cms-page .steps-hero .fb-controls .fb-tab,
.cms-page .steps-hero .fb-controls .fb-seg span,
.cms-page .steps-hero .fb-controls .fb-check,
.cms-page .steps-hero .fb-controls .sw,
.cms-page .steps-hero .embed-choices .embed-chip {
  cursor: default;
  pointer-events: none;
  user-select: none;
}

.cms-page .steps-hero .fb-seg {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
}

.cms-page .steps-hero .fb-seg span {
  align-items: center;
  display: inline-flex;
  flex: 1 1 86px;
  justify-content: center;
  min-width: min(100%, 86px);
  text-align: center;
  white-space: nowrap;
}

.cms-page .step-row[data-step="3"] .embed-chip-ic {
  align-items: center;
  background: #0f766e;
  border-color: #0f766e;
  color: #fff;
  display: inline-flex;
  justify-content: center;
}

.cms-page .step-row[data-step="3"] .embed-chip-s {
  color: #334155;
  font-weight: 600;
}

@media (max-width: 1100px) {
  .cms-page:not(.cms-page--home) .steps-hero .step-row {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    justify-items: center;
    gap: 16px !important;
    padding: 28px 0 !important;
    text-align: center;
  }

  .cms-page:not(.cms-page--home) .steps-hero .step-num-big {
    grid-column: 1;
    grid-row: 1;
    margin-left: auto;
    margin-right: auto;
  }

  .cms-page:not(.cms-page--home) .steps-hero .step-body {
    grid-column: 1;
    grid-row: 2;
    width: 100%;
  }

  .cms-page:not(.cms-page--home) .steps-hero .step-grid {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 20px !important;
  }

  .cms-page:not(.cms-page--home) .steps-hero .step-grid > *,
  .cms-page:not(.cms-page--home) .steps-hero .sm-signup,
  .cms-page:not(.cms-page--home) .steps-hero .fb-mock,
  .cms-page:not(.cms-page--home) .steps-hero .embed-snippet,
  .cms-page:not(.cms-page--home) .steps-hero .embed-choices,
  .cms-page:not(.cms-page--home) .steps-hero .dash-mock {
    max-width: 560px;
    min-width: 0;
    width: 100%;
  }

  .cms-page:not(.cms-page--home) .steps-hero .fb-mock,
  .cms-page:not(.cms-page--home) .steps-hero .embed-choices {
    grid-template-columns: minmax(0, 1fr) !important;
  }

  .cms-page:not(.cms-page--home) .steps-hero .sm-connect-stripe {
    flex-wrap: wrap;
  }

  .cms-page:not(.cms-page--home) .steps-hero .sm-connect-stripe .ok {
    margin-left: 0;
  }
}

@media (max-width: 960px) {
  .cms-page .section-head,
  .cms-page .pblock > div:first-child,
  .cms-page .pblock-inner > div:first-child,
  .cms-page .mc-section__header,
  .cms-page .faq-head,
  .cms-page .final,
  .cms-page .step-body {
    text-align: center;
  }

  .cms-page .section-eyebrow,
  .cms-page .pblock-kicker,
  .cms-page .cms-page__eyebrow,
  .cms-page .kick,
  .cms-page .tag,
  .cms-page .step-kicker {
    display: inline-flex;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .section-head h2,
  .cms-page .pblock h2,
  .cms-page .mc-section__title,
  .cms-page .faq h2,
  .cms-page .final h2,
  .cms-page .step-h {
    text-align: center;
  }

  .cms-page .section-lede,
  .cms-page .pblock .lede,
  .cms-page .step-lede,
  .cms-page .final p {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .step-row {
    justify-items: center;
  }

  .cms-page .step-num-big {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    font-size: 18px;
    line-height: 1;
  }

  .cms-page .btn {
    width: 100%;
  }

  .cms-page .cta-row {
    align-items: stretch;
    flex-direction: column;
  }

  .cms-page .fb-panel {
    align-items: stretch;
    width: 100%;
  }

  .cms-page .fb-seg {
    width: 100%;
  }

  .cms-page .fb-seg span {
    flex: 1 1 0;
  }

  .cms-page .fb-swatches {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    width: 100%;
  }

  .cms-page .fb-swatches .sw {
    justify-self: center;
  }

  .cms-page .step-row[data-step="3"] .step-grid > div:last-child,
  .cms-page .step-row[data-step="3"] .embed-snippet,
  .cms-page .step-row[data-step="3"] .embed-chip {
    text-align: left;
  }

  .cms-page .step-row[data-step="3"] .embed-chip-ic {
    margin-left: 0;
    margin-right: 0;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-row {
    align-items: start !important;
    display: grid !important;
    gap: 4px 10px !important;
    grid-template-columns: minmax(0, 1fr) auto;
    padding: 10px 0 !important;
    text-align: left;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .nm {
    align-items: start !important;
    display: grid !important;
    gap: 0 8px !important;
    grid-template-columns: 22px minmax(0, 1fr);
    min-width: 0;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-name,
  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-amt {
    overflow-wrap: anywhere;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-name {
    color: var(--ink);
    font-weight: 700;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-amt {
    color: var(--ink-2);
    font-weight: 600;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .pl {
    grid-column: 1 / -1;
    padding-left: 30px;
  }

  .cms-page .step-row[data-step="4"] .dash-mock-body .bo-pill {
    align-self: start;
    grid-column: 2;
    grid-row: 1;
    white-space: nowrap;
  }
}
`

const defaultHowItWorksFaqItems = [
  {
    question: "Do I need a Stripe account before I start?",
    answer:
      "No. You can create a Stripe account after you create a MoonClerk account and when you are ready to activate — it takes about 3 minutes and requires your business info and a bank account for payouts. If you already have a Stripe account, you connect it in one click.",
  },
  {
    question: "Can I really get to first payment in 5 minutes?",
    answer:
      "The median across our last 500 signups was under 5 minutes from account creation to first live form. Getting a customer to actually pay depends on when you share the link — but the form itself is genuinely under 10 minutes.",
  },
  {
    question: "What do I need before I start?",
    answer:
      "MoonClerk only requires a company name and email address. All US Stripe accounts must meet these requirements: unregistered businesses must have a business owner or representative physically located in the US; registered businesses must be registered in the US, even if you operate in another country; and all businesses must provide the address of the physical location where the majority of your business activity is carried out (PO Boxes are not accepted). Stripe will verify your legal entity name, legal entity type (such as Sole Proprietor or LLC), EIN, SSN (or ITIN), and business address. Stripe may ask for official documentation to help verify these details.",
  },
  {
    question: "What happens if I hit a wall?",
    answer:
      "Email help@moonclerk.com and a real person will help, typically same-day. We don't use chatbots.",
  },
  {
    question: "Can I migrate from another platform?",
    answer:
      "There is no self-serve importer today. If you process more than $10K/month, email us and we can review whether we can help with the migration based on complexity.",
  },
]

const HowItWorksPage = ({ data }) => {
  const page = data.allDatoCmsMcV2PageMarketing.nodes[0] || {}
  const sectionCollection =
    page.sectionCollection ||
    pickCollectionNode(
      data.allDatoCmsMcV2SectionCollection.nodes,
      null,
      "how-it-works-sections"
    )
  const howItWorksCollection =
    page.howItWorksCollection ||
    pickCollectionNode(
      data.allDatoCmsMcV2HowItWorksCollection.nodes,
      null,
      "how-it-works-main"
    )
  const faqCollection =
    page.faqCollection ||
    resolveFaqCollection(data.allDatoCmsMcV2FaqCollection.nodes, null, "how-it-works-faq")
  const hasCanonicalContent = Boolean(page.contentHtml)
  const resolvedFaqItems = Array.isArray(faqCollection.faqItems) ? faqCollection.faqItems : []
  const faqItems = hasCanonicalContent
    ? []
    : resolvedFaqItems.length > 0
      ? resolvedFaqItems
      : defaultHowItWorksFaqItems
  const baseContentSections = sectionCollection.contentSections || []
  const hasFaqSection = baseContentSections.some(
    section => section?.componentType === "faq_list" || section?.sectionKey === "how_it_works_faq"
  )
  const faqSection = {
    sectionKey: "how_it_works_faq",
    componentType: "faq_list",
    hidden: false,
    eyebrow: "FAQ",
    headingHtml: "How-it-works questions.",
    summary: "",
    contentItemEntries: [],
  }
  const contentSections = hasFaqSection
    ? baseContentSections
    : baseContentSections.reduce((sections, section) => {
        if (section?.sectionKey === "how_it_works_final") {
          return [...sections, faqSection, section]
        }

        return [...sections, section]
      }, [])
  const heroSlot = (
    <section className="hero-f cms-page__hero" style={{ padding: "80px 32px 56px" }}>
      <div
        className="hero-f-inner cms-page__hero-inner cms-page__hero-inner--centered"
        style={{ maxWidth: "780px", textAlign: "center", gap: 0 }}
      >
        <div className="cms-page__hero-copy" style={{ margin: "0 auto" }}>
          {page.eyebrow ? (
            <div className="cms-page__eyebrow" style={{ margin: "0 auto 20px" }}>
              {page.eyebrow}
            </div>
          ) : null}
          {page.heading ? <h1 dangerouslySetInnerHTML={{ __html: page.heading }} /> : null}
          {page.summary ? (
            <p
              className="cms-page__summary"
              style={{ margin: "0 auto 12px", maxWidth: "620px" }}
            >
              {page.summary}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      heroSlot={heroSlot}
      eyebrow={page.eyebrow}
      heading={page.heading}
      summary={page.summary}
      body={page.body}
      bodyHeading={page.bodyHeading || "How It Comes Together"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      contentSections={contentSections}
      howItWorksSteps={hasCanonicalContent ? [] : howItWorksCollection.stepEntries || []}
      howItWorksStats={hasCanonicalContent ? [] : howItWorksCollection.statEntries || []}
      faqItems={faqItems}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={`${prototypeSlice.extraStyles || ""}\n${howItWorksMobileStyles}`}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the how-it-works record in mc_v2_page_marketing."
    />
  )
}

export default HowItWorksPage

export const query = graphql`
  query HowItWorksCmsPageQuery {
    allDatoCmsMcV2PageMarketing(filter: { slug: { eq: "how-it-works" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
          }
        }
        howItWorksCollection {
          stepEntries {
            rank
            kicker
            timeLabel
            headingHtml
            lede
            bulletItemsText
            mockKey
          }
          statEntries {
            rank
            displayValue
            counterTarget
            counterPrefix
            counterSuffix
            counterDecimals
            label
          }
        }
        faqCollection {
          sourceCollection {
            key
          }
          selectedFaqEntries {
            question
            answer
          }
        }
      }
    }
    allDatoCmsMcV2SectionCollection {
      nodes {
        key
        contentSections {
          ...CmsHtmlSectionFields
        }
      }
    }
    allDatoCmsMcV2HowItWorksCollection {
      nodes {
        key
        stepEntries {
          rank
          kicker
          timeLabel
          headingHtml
          lede
          bulletItemsText
          mockKey
        }
        statEntries {
          rank
          displayValue
          counterTarget
          counterPrefix
          counterSuffix
          counterDecimals
          label
        }
      }
    }
    allDatoCmsMcV2FaqCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedFaqEntries {
          question
          answer
        }
      }
    }
  }
`
